"use client";

import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import {
  GROWTH_DASHED_PATH,
  GROWTH_NOW_X,
  GROWTH_NOW_Y,
  GROWTH_SOLID_PATH,
  GROWTH_VIEWBOX,
} from "./developers-growth-chart-path";

const YEARS: readonly string[] = ["2026", "2027", "2028", "2029"];
const YEAR_X: readonly number[] = [70, 383.3, 696.7, 1010];
const PLOT_WIDTH = 1040;
const PLOT_HEIGHT = 560;
const DASH_REVEAL_WIDTH = PLOT_WIDTH - GROWTH_NOW_X;

/**
 * Exponential users/revenue chart. Dashed projection draws once on first view.
 */
export function DevelopersGrowthChart({
  clipId = "dvf-growth-dash-reveal",
  calloutLabel = "JOIN US HERE",
}: {
  readonly clipId?: string;
  readonly calloutLabel?: string;
}): ReactElement {
  const t = useTranslations("public.developersGrowthChart");
  const rootRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState<boolean>(false);
  const [playId, setPlayId] = useState<number>(0);

  useEffect(() => {
    const root: HTMLDivElement | null = rootRef.current;
    if (root === null) return;

    const observer: IntersectionObserver = new IntersectionObserver(
      (entries: readonly IntersectionObserverEntry[]): void => {
        const entry: IntersectionObserverEntry | undefined = entries[0];
        if (entry === undefined) return;
        if (entry.isIntersecting) {
          setPlayId((current: number): number => current + 1);
          setInView(true);
          return;
        }
        setInView(false);
      },
      { threshold: 0.35 },
    );
    observer.observe(root);
    return (): void => {
      observer.disconnect();
    };
  }, []);

  const chartClassName: string = inView
    ? "dvf-growth-chart dvf-growth-inview"
    : "dvf-growth-chart";

  return (
    <div className={chartClassName} ref={rootRef}>
      <div className="dvf-growth-yaxis-wrap">
        <span className="dvf-growth-yaxis">{t("usersAmpRevenue")}</span>
      </div>
      <div className="dvf-growth-plot">
        <svg
          viewBox={GROWTH_VIEWBOX}
          className="dvf-growth-svg"
          role="img"
          aria-label="Users and revenue growing from 2026 toward 2029"
        >
          <defs>
            <clipPath id={clipId}>
              <rect
                key={playId}
                className="dvf-growth-dash-reveal"
                x={GROWTH_NOW_X}
                y="0"
                width={DASH_REVEAL_WIDTH}
                height={PLOT_HEIGHT}
              />
            </clipPath>
          </defs>
          <g stroke="var(--dvf-line-soft)" strokeWidth="1">
            <path d="M70 32 H1010" />
            <path d="M70 144.5 H1010" />
            <path d="M70 257 H1010" />
            <path d="M70 369.5 H1010" />
            <path d="M383.3 32 V488" />
            <path d="M696.7 32 V488" />
            <path d="M1010 32 V488" />
          </g>
          <path
            d="M70 488 H1010"
            stroke="var(--dvf-line)"
            strokeWidth="1.5"
            fill="none"
          />
          <path
            d={GROWTH_SOLID_PATH}
            fill="none"
            stroke="#2EC4A8"
            strokeWidth="5"
            strokeLinecap="round"
          />
          <g clipPath={`url(#${clipId})`}>
            <path
              d={GROWTH_DASHED_PATH}
              fill="none"
              stroke="#2EC4A8"
              strokeOpacity="0.45"
              strokeWidth="5"
              strokeLinecap="round"
              strokeDasharray="15 15"
            />
          </g>
          <circle
            cx={GROWTH_NOW_X}
            cy={GROWTH_NOW_Y}
            r="7.5"
            fill="var(--dvf-elevated)"
            stroke="#2EC4A8"
            strokeWidth="3.5"
          />
          <g
            fontSize="16"
            fontWeight="600"
            fill="var(--dvf-ink-mute)"
            textAnchor="middle"
            fontFamily="Inter, ui-sans-serif, system-ui, sans-serif"
          >
            {YEARS.map((year: string, index: number) => {
              const x: number = YEAR_X[index] ?? 70;
              return (
                <text key={year} x={x} y="534">
                  {year}
                </text>
              );
            })}
          </g>
        </svg>
        <div className="dvf-growth-callout" aria-hidden={true}>
          <span className="dvf-growth-callout-label">{calloutLabel}</span>
          <span className="dvf-growth-callout-stem" />
        </div>
      </div>
    </div>
  );
}
